import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getImage } from "redux/slices/getImageSlice";
import { getPrimaryOwnerFromParties, isAgentPortal } from "js/utilities";
import {
  base64ToArrayBuffer,
  convertToTitleCase,
  getName,
  getPolicyNumberTitle,
} from "js/formatting";
import { useTranslation } from "react-i18next";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";

import useTrackManageAutoPay from "logging/hooks/useTrackManageAutoPay";
import PDFDialog from "components/ModalDialogs/PDFDialog";
import Badge from "components/General/Badge";
import LoadingOverlay from "react-loading-overlay";
import * as fileSaver from "file-saver";
import moment from "moment";

export default function MessageRow({ message, policy, hasDescription = true }) {
  const { t } = useTranslation();
  const { user } = useSelector((s) => s.user);
  let trackManageAutoPay = useTrackManageAutoPay();
  const [pdfDialogState, setPdfDialogState] = useState(false);
  const [pdfSource, setPdfSource] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const getTitle = (message) => {
    if (message?.document?.typeOfDocument === "COMPANY ALERTS")
      return convertToTitleCase(message?.title);
    else if (message?.document?.typeOfDocument === "OBMAIL")
      return "Correspondence";
    else if (message?.document?.typeOfDocument === "Consent")
      return "Electronic Communication Consent";
    return message?.document?.typeOfDocument;
  };
  const getSublTitle = (message) => {
    if (message?.document?.typeOfDocument === "Company Alert")
      return moment(message?.startDate).format("MM/DD/YYYY");
    return message?.document?.dateOfDocument;
  };

  const getMimeTypeFromContent = (content) => {
    if (content.startsWith("JVBERi0")) {
      return "application/pdf";
    } else if (content.startsWith("SUkq")) {
      return "image/tiff";
    } else if (content.startsWith("/9j/")) {
      return "image/jpeg";
    } else if (content.startsWith("iVBORw0KGgo")) {
      return "image/png";
    } else {
      return "application/octet-stream"; // Default for unknown types
    }
  };

  return (
    <>
      <LoadingOverlay
        active={isLoading}
        spinner={
          <EverlakeLoadingSpinner
            loadingText={t("AlertsCorrespondence-messageRow-loading-text")}
          />
        }
        styles={EverlakeLoadingOverlayStyle}
      >
        <div
          className={`${
            message.status ? `ui-badge-bar-left-${message.status}` : ""
          } relative flex flex-col py-4 pl-6 pr-4 bg-white border-b border-theme-rule-medium md:pl-8 lg:pl-12`}
        >
          <div className="flex flex-col-reverse md:flex-row justify-between">
            <div className="text-theme-text-secondary font-semibold flex flex-row items-center">
              {message.status && (
                <div className="mr-2">
                  <Badge
                    iconOnly={true}
                    status={message.status}
                    statusText={message.title}
                    srPrefix="Message"
                  />
                </div>
              )}
              <div>
                <div>{getTitle(message)}</div>
              </div>
            </div>
            <div className="text-xxs font-bold text-right">
              {getSublTitle(message)}
            </div>
          </div>
          <div className="font-bold text-xxs mt-2">
            {message.subject && (
              <span className="mr-4">
                {t("AlertsCorrespondence-messageRow-field-text-1")}
                {message.subject}
              </span>
            )}
            {message?.document?.typeOfDocument?.toUpperCase() !==
              "COMMISSIONS" &&
              message?.type !== "Company Alert" && (
                <span className="">
                  {getPolicyNumberTitle(policy)} {message.oldPolicy}
                </span>
              )}
            {message?.document?.typeOfDocument?.toUpperCase() ===
              "COMMISSIONS" && (
              <span className="">
                {t("AlertsCorrespondence-messageRow-field-text-2")}
                {message.alternateAgentId}
              </span>
            )}
          </div>
          {hasDescription && (
            <div className="text-xxs min-h-8 mt-2 break-all">
              {message.document.description}
            </div>
          )}
          {message?.type !== "alert" && message?.type !== "Company Alert" && (
            <div className={`${!hasDescription ? "flex" : ""}`}>
              <button
                className={`ui-btn-primary mt-4 ${
                  !hasDescription ? "ml-auto w-32" : ""
                }`}
                onClick={() => {
                  setIsLoading(true);
                  dispatch(
                    getImage({
                      imageFileName: message.document.name,
                      policyId: message.policy.contractID,
                    })
                  )
                    .then((response) => {
                      // setPdfDialogState(true);
                      setIsLoading(false);
                      // setPdfSource(response.payload.document.contentStream.type);
                      const contentStream =
                        response.payload.document.contentStream.type;
                      const mimeType = getMimeTypeFromContent(contentStream);
                      let byteArray = base64ToArrayBuffer(contentStream);
                      let blob = new Blob([byteArray], { type: mimeType });
                      fileSaver.saveAs(blob, `${message.document.name}`);
                      let primaryOwnerParty = isAgentPortal()
                        ? policy?.parties?.find(
                            (p) =>
                              p.person?.type?.toLowerCase() === "primary owner"
                          )
                        : getPrimaryOwnerFromParties(policy?.parties);
                      // EUEC-4529 - Whenever user downloads the correspondence make an entry in AppInSights
                      trackManageAutoPay({
                        name: "Alerts & Documents",
                        policyHolderName: getName(primaryOwnerParty?.person),
                        dob: primaryOwnerParty?.person?.dateofBirth ?? "",
                        userFirstName: user?.firstName,
                        userLastName: user?.lastName,
                        policyNumber: message?.policy?.contractID,
                        fileName: message.document.name,
                        documentType: message.document.typeOfDocument,
                        userName: user?.userName,
                        portal: isAgentPortal() ? "Agent" : "Customer",
                      });
                    })
                    .catch((error) => {
                      setIsLoading(false);
                      console.error(error);
                    });
                }}
              >
                {t("AlertsCorrespondence-messageRow-download-pdf")}
              </button>
            </div>
          )}
        </div>
      </LoadingOverlay>
      <PDFDialog
        title={`${message.document.name ?? ""} Preview`}
        description={message.document.description}
        pdfSource={pdfSource}
        dialogState={pdfDialogState}
        setDialogState={setPdfDialogState}
      />
    </>
  );
}
